/**
 * GatewayDetail — full gateway management page for a property.
 * Installer-only. Shows health, commands, diagnostics, lifecycle actions.
 * Also handles commissioning view when device is in commissioning state.
 */
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { useGatewayDevice } from '@/hooks/useGatewayDevice';
import { Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { GatewayHealthDetail } from '@/components/gateway/GatewayHealthDetail';
import { GatewayBusDeviceList } from '@/components/gateway/GatewayBusDeviceList';
import { CommandPanel } from '@/components/gateway/CommandPanel';
import { DiagnosticViewer } from '@/components/gateway/DiagnosticViewer';
import { DeviceManagementActions } from '@/components/gateway/DeviceManagementActions';
import { CommissioningView } from '@/components/gateway/CommissioningView';
import { useGatewayDeviceConfigs } from '@/hooks/useGatewayDeviceConfigs';
import { LIFECYCLE_STATUS_CONFIG, OPERATIONAL_STATUS_CONFIG } from '@/types/gateway';
import { Badge } from '@/components/ui/badge';

export default function GatewayDetail() {
  const { propertyId } = useParams<{ propertyId: string }>();
  const navigate = useNavigate();
  const { properties, isInstaller, isHomeowner, loading: appLoading } = useApp();
  const { device, isLoading, refetch } = useGatewayDevice(propertyId);
  const { configs, isLoading: configsLoading } = useGatewayDeviceConfigs(device?.id);

  const property = properties.find((p) => p.id === propertyId);

  // Homeowner guard — redirect
  if (!appLoading && isHomeowner) {
    navigate(`/app/property/${propertyId}`, { replace: true });
    return null;
  }

  if (appLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-6 h-6 animate-spin text-wt-text-secondary" />
      </div>
    );
  }

  if (!device) {
    return (
      <div className="space-y-4 px-6 py-5 max-w-[1200px] mx-auto">
        <Button variant="ghost" size="sm" onClick={() => navigate(`/app/property/${propertyId}`)}>
          <ArrowLeft className="w-4 h-4 mr-1.5" />
          Back to Property
        </Button>
        <div className="py-12 text-center text-sm text-wt-text-secondary">
          No gateway linked to this property.
        </div>
      </div>
    );
  }

  const lifecycle = LIFECYCLE_STATUS_CONFIG[device.lifecycle_status];
  const operational = OPERATIONAL_STATUS_CONFIG[device.operational_status];

  // Commissioning view takes over
  if (device.lifecycle_status === 'commissioning') {
    return (
      <div className="space-y-4 px-6 py-5 max-w-[1200px] mx-auto">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/app/property/${propertyId}`)}>
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Back
          </Button>
          <h1 className="text-base font-semibold text-wt-text-primary">
            {property?.name ?? 'Property'} — Gateway
          </h1>
        </div>
        <CommissioningView device={device} />
      </div>
    );
  }

  return (
    <div className="space-y-4 px-6 py-5 max-w-[1200px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(`/app/property/${propertyId}`)}>
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            Back
          </Button>
          <h1 className="text-base font-semibold text-wt-text-primary">
            {property?.name ?? 'Property'} — Gateway
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={lifecycle.color}>{lifecycle.label}</Badge>
          <div className="flex items-center gap-1.5">
            <span className={`w-2 h-2 rounded-full ${operational.dotColor}`} />
            <span className={`text-xs ${operational.textColor}`}>{operational.label}</span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="health">
        <TabsList className="bg-wt-bg-primary border border-border">
          <TabsTrigger value="health">Health</TabsTrigger>
          <TabsTrigger value="devices">Devices</TabsTrigger>
          <TabsTrigger value="commands">Commands</TabsTrigger>
          <TabsTrigger value="diagnostics">Diagnostics</TabsTrigger>
          <TabsTrigger value="manage">Manage</TabsTrigger>
        </TabsList>

        <TabsContent value="health" className="mt-4">
          <GatewayHealthDetail device={device} isLoading={isLoading} />
        </TabsContent>

        <TabsContent value="devices" className="mt-4">
          <GatewayBusDeviceList configs={configs} isLoading={configsLoading} />
        </TabsContent>

        <TabsContent value="commands" className="mt-4">
          <CommandPanel device={device} onDeviceUpdate={refetch} />
        </TabsContent>

        <TabsContent value="diagnostics" className="mt-4">
          <DiagnosticViewer device={device} />
        </TabsContent>

        <TabsContent value="manage" className="mt-4">
          <DeviceManagementActions
            device={device}
            propertyName={property?.name ?? 'Property'}
            onDeviceUpdate={refetch}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
